/**
 * Provider-agnostic captcha execution.
 *
 * Call sites ask for a token and get back `{ token, version }`. The version is
 * passed straight through to the API, which branches on it server-side — that is
 * what lets both providers run at once so we can ramp or roll back Turnstile with
 * a config change rather than a deploy.
 *
 * Deliberately imperative rather than a React component: it creates and tears down
 * its own widget per call, so there is no mounted-widget lifecycle to coordinate
 * across the two payment surfaces (checkout and billing settings), and no way to
 * accidentally submit an already-spent token. Turnstile tokens are single-use.
 */

interface TurnstileRenderOptions {
  sitekey: string;
  /** Analytics identifier, surfaced back to us in the siteverify response. Max 32 chars, `[A-Za-z0-9_-]`. */
  action?: string;
  cData?: string;
  callback?: (token: string) => void;
  "error-callback"?: (code?: string) => void;
  "expired-callback"?: () => void;
  "timeout-callback"?: () => void;
  "unsupported-callback"?: () => void;
  "before-interactive-callback"?: () => void;
  "after-interactive-callback"?: () => void;
  execution?: "render" | "execute";
  appearance?: "always" | "execute" | "interaction-only";
  retry?: "auto" | "never";
  "retry-interval"?: number;
  "refresh-expired"?: "auto" | "manual" | "never";
  "response-field"?: boolean;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "flexible" | "compact";
  language?: string;
}

interface TurnstileApi {
  render(container: string | HTMLElement, options: TurnstileRenderOptions): string | undefined;
  execute(container: string | HTMLElement, options?: TurnstileRenderOptions): void;
  reset(widgetId?: string): void;
  remove(widgetId?: string): void;
  getResponse(widgetId?: string): string | undefined;
}

interface GrecaptchaApi {
  ready(callback: () => void): void;
  execute(sitekey: string, options: { action: string }): Promise<string>;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    grecaptcha?: GrecaptchaApi;
  }
}

/**
 * Action names the API matches exactly and case-sensitively against the action the
 * widget was configured with. A token minted for one action cannot be spent on
 * another, so a mismatch fails verification — and while enforcement is in monitor
 * mode it does so silently, logging as a rejection with no client-side symptom.
 *
 * Typed as a closed set so a typo is a compile error rather than a runtime mystery.
 * Kept in lockstep with the API's list; see captcha.test.ts.
 */
export const CAPTCHA_ACTIONS = {
  register: "register",
  addPaymentMethod: "addPaymentMethod",
  checkoutSignup: "checkoutSignup",
  reactivateSubscription: "reactivateSubscription",
  login: "login",
  resend: "resend",
  recover: "recover",
  addEmail: "addEmail",
} as const;

export type CaptchaAction = (typeof CAPTCHA_ACTIONS)[keyof typeof CAPTCHA_ACTIONS];

/**
 * Compile-time pin on the four action strings.
 *
 * The union type above stops a call site inventing an action, but not someone
 * editing a value here — and a drifted value fails verification silently while
 * enforcement is in monitor mode, so it would not show up as a broken build or a
 * broken page. This makes it show up as a type error instead. There is no test
 * runner in this package; if one is added, move this to a real test.
 *
 * If you are changing these, the API's list has to change in the same breath.
 */
type Exact<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;

// Must be an assignment, not an `as` assertion: `never` is assignable to `true`, so
// an assertion would silently permit the very drift this is meant to catch.
const ACTIONS_ARE_PINNED: Exact<
  typeof CAPTCHA_ACTIONS,
  {
    readonly register: "register";
    readonly addPaymentMethod: "addPaymentMethod";
    readonly checkoutSignup: "checkoutSignup";
    readonly reactivateSubscription: "reactivateSubscription";
    readonly login: "login";
    readonly resend: "resend";
    readonly recover: "recover";
    readonly addEmail: "addEmail";
  }
> = true;
void ACTIONS_ARE_PINNED;

export type CaptchaVersion = "v3" | "turnstile";

export interface CaptchaResult {
  token: string;
  version: CaptchaVersion;
}

/** Coarse reason a captcha attempt failed. Logged, never shown to the user verbatim. */
export type CaptchaFailure =
  | "config"
  | "unavailable"
  | "script-load"
  | "challenge-failed"
  | "expired"
  | "timeout"
  | "unsupported"
  | "cancelled";

export interface CaptchaError extends Error {
  captchaFailure: CaptchaFailure;
}

const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/**
 * Ceiling on an attempt before Turnstile has told us a human is involved.
 *
 * Generous on purpose: a visible challenge can appear without
 * before-interactive-callback firing, so this budget has to be long enough not to
 * cut someone off mid-solve. It stays bounded so a broken widget still fails rather
 * than hanging, and Escape cancels at any point.
 */
const UNATTENDED_TIMEOUT_MS = 60_000;
const SCRIPT_LOAD_TIMEOUT_MS = 10_000;

/**
 * Ceiling on a challenge a human is working through. Turnstile's own timeout-callback
 * normally covers this; the backstop exists so a challenge that never paints can't
 * leave the visitor staring at a backdrop with no way out.
 */
const INTERACTIVE_TIMEOUT_MS = 120_000;

/**
 * Window allowed for Turnstile's own retry after it reports an error, before we give
 * up and surface the code. Comfortably longer than its default 8s retry interval.
 */
const ERROR_GRACE_MS = 12_000;

/** Above the app's modal portals, which is where an interactive challenge has to sit. */
const OVERLAY_Z_INDEX = 2147483000;

function captchaError(failure: CaptchaFailure, detail: string): CaptchaError {
  const error = new Error(`captcha: ${failure} (${detail})`) as CaptchaError;
  error.captchaFailure = failure;
  return error;
}

/** True when the visitor backed out of a challenge themselves, rather than it failing. */
export function isCaptchaCancellation(err: unknown): boolean {
  return (err as CaptchaError | undefined)?.captchaFailure === "cancelled";
}

// Provider-neutral: the user doesn't care which vendor scored them, and the wording
// shouldn't need changing when the provider flips.
export const CAPTCHA_ERROR_MESSAGE = "We couldn't verify your request. Please try again.";

/**
 * Message to show for a failed captcha, or null when there is nothing to say —
 * a visitor who dismissed the challenge themselves already knows why nothing
 * happened, so that case returns the form to idle rather than accusing them.
 */
export function captchaFailureMessage(err: unknown, action: CaptchaAction): string | null {
  if (isCaptchaCancellation(err)) return null;
  console.error(`Captcha failed for ${action}`, err);
  return CAPTCHA_ERROR_MESSAGE;
}

function getProvider(): "recaptcha" | "turnstile" {
  return process.env.UGG_PUBLIC_CAPTCHA_PROVIDER === "turnstile" ? "turnstile" : "recaptcha";
}

// `typeof` guards rather than the `global` package so the DOM types survive; both are
// safe in the SSR bundle, where neither identifier is declared.
function getWindow(): Window | undefined {
  return typeof window === "undefined" ? undefined : window;
}

function getDocument(): Document | undefined {
  return typeof document === "undefined" ? undefined : document;
}

/**
 * Acquire a captcha token for `action`.
 *
 * Always settles: every path either resolves with a token or rejects with a
 * CaptchaError. Callers must reset their own loading state in both cases.
 */
export function executeCaptcha(action: CaptchaAction): Promise<CaptchaResult> {
  return getProvider() === "turnstile" ? executeTurnstile(action) : executeRecaptchaV3(action);
}

/* -------------------------------------------------------------------------- */
/* reCAPTCHA v3                                                               */
/* -------------------------------------------------------------------------- */

function executeRecaptchaV3(action: string): Promise<CaptchaResult> {
  return new Promise<CaptchaResult>((resolve, reject) => {
    const sitekey = process.env.UGG_PUBLIC_RECAPTCHA_SITEKEY_V3;
    if (!sitekey) {
      reject(captchaError("config", "UGG_PUBLIC_RECAPTCHA_SITEKEY_V3 is not set"));
      return;
    }

    const grecaptcha = getWindow()?.grecaptcha;
    if (!grecaptcha) {
      reject(captchaError("unavailable", "window.grecaptcha is not present"));
      return;
    }

    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(captchaError("timeout", `no token after ${UNATTENDED_TIMEOUT_MS}ms`));
    }, UNATTENDED_TIMEOUT_MS);

    const succeed = (token: string) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve({ token, version: "v3" });
    };

    const fail = (failure: CaptchaFailure, detail: string) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(captchaError(failure, detail));
    };

    try {
      grecaptcha.ready(() => {
        // The `.catch` matters: without it a rejection here (extension interference,
        // network blip) settles nothing and leaves the caller spinning forever.
        grecaptcha
          .execute(sitekey, { action })
          .then(succeed)
          .catch((err: unknown) => fail("challenge-failed", String(err)));
      });
    } catch (err) {
      fail("unavailable", String(err));
    }
  });
}

/* -------------------------------------------------------------------------- */
/* Cloudflare Turnstile                                                       */
/* -------------------------------------------------------------------------- */

let turnstileScriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (turnstileScriptPromise) return turnstileScriptPromise;

  turnstileScriptPromise = new Promise<void>((resolve, reject) => {
    const doc = getDocument();
    if (!doc) {
      reject(captchaError("unavailable", "no document"));
      return;
    }
    if (getWindow()?.turnstile) {
      resolve();
      return;
    }

    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let scriptEl: HTMLScriptElement | null = null;

    const bail = (detail: string) => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      if (scriptEl?.parentNode) scriptEl.parentNode.removeChild(scriptEl);
      reject(captchaError("script-load", detail));
    };

    timer = setTimeout(() => bail(`script did not load within ${SCRIPT_LOAD_TIMEOUT_MS}ms`), SCRIPT_LOAD_TIMEOUT_MS);

    const done = () => {
      if (settled) return;
      // api.js?render=explicit defines window.turnstile as it loads; poll briefly rather
      // than trusting onload ordering.
      let attempts = 0;
      const poll = () => {
        if (settled) return;
        if (getWindow()?.turnstile) {
          settled = true;
          if (timer !== undefined) clearTimeout(timer);
          resolve();
          return;
        }
        if (attempts++ > 50) {
          bail("script loaded but window.turnstile is absent");
          return;
        }
        setTimeout(poll, 50);
      };
      poll();
    };

    const failed = () => bail("script failed to load");

    const existing = doc.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
    if (existing) {
      scriptEl = existing;
      existing.addEventListener("load", done);
      existing.addEventListener("error", failed);
      // Already-loaded scripts fire nothing, so probe immediately too.
      done();
      return;
    }

    const script = doc.createElement("script");
    scriptEl = script;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", done);
    script.addEventListener("error", failed);
    doc.head.appendChild(script);
  }).catch((err) => {
    // Drop the memo so a later attempt can retry rather than inheriting this failure.
    turnstileScriptPromise = null;
    throw err;
  });

  return turnstileScriptPromise;
}

interface Overlay {
  mount: HTMLElement;
  reveal: () => void;
  conceal: () => void;
  destroy: () => void;
}

/**
 * Host element for the widget.
 *
 * Present in the layout but transparent and click-through until Turnstile signals it
 * needs the user — Turnstile refuses to run in a hidden container, so this can't use
 * `display:none`. Turnstile has no overlay mode of its own; an interaction-only widget
 * renders inline, so we supply the backdrop and centering ourselves.
 */
function createOverlay(doc: Document, onDismiss: () => void): Overlay {
  const overlay = doc.createElement("div");
  overlay.setAttribute("data-ugg-captcha", "");
  overlay.style.cssText = [
    "position:fixed",
    "top:0",
    "right:0",
    "bottom:0",
    "left:0",
    "display:flex",
    "align-items:center",
    "justify-content:center",
    `z-index:${OVERLAY_Z_INDEX}`,
    "pointer-events:none",
    "background-color:transparent",
    "transition:background-color 120ms ease",
  ].join(";");

  const mount = doc.createElement("div");
  // The widget itself is ALWAYS clickable. Turnstile can render a visible challenge
  // without firing before-interactive-callback — the "visible" test sitekeys do exactly
  // that — and if interactivity were gated on that callback, the visitor would see
  // "Verify you're human" while their clicks passed through to the page underneath. The
  // challenge would then never complete and the caller would hang until a timeout.
  mount.style.pointerEvents = "auto";
  // Don't let the centring flex container shrink the widget: the mount has no
  // intrinsic width until Turnstile's iframe loads, and a collapsed container gives
  // the visitor a challenge they cannot click.
  mount.style.flex = "0 0 auto";
  overlay.appendChild(mount);
  doc.body.appendChild(overlay);

  // Escape hatch, live only while the challenge is actually on screen. Binding it for
  // the widget's whole lifetime would let an unrelated Escape — closing a modal, say —
  // silently cancel a captcha the visitor cannot even see.
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") onDismiss();
  };
  const onClick = (event: MouseEvent) => {
    // Only the backdrop itself — never a click that landed on the widget.
    if (event.target === overlay) onDismiss();
  };

  let shown = false;
  const bind = () => {
    doc.addEventListener("keydown", onKeyDown);
    overlay.addEventListener("click", onClick);
  };
  const unbind = () => {
    doc.removeEventListener("keydown", onKeyDown);
    overlay.removeEventListener("click", onClick);
  };

  return {
    mount,
    // Idempotent: called both from before-interactive-callback and from the visibility
    // observer, whichever notices first.
    reveal: () => {
      if (shown) return;
      shown = true;
      overlay.style.pointerEvents = "auto";
      overlay.style.backgroundColor = "rgba(0,0,0,0.6)";
      bind();
    },
    conceal: () => {
      if (!shown) return;
      shown = false;
      overlay.style.pointerEvents = "none";
      overlay.style.backgroundColor = "transparent";
      unbind();
    },
    destroy: () => {
      unbind();
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    },
  };
}

function createInlineHost(container: HTMLElement): Overlay {
  const mount = container.ownerDocument.createElement("div");
  container.appendChild(mount);
  return {
    mount,
    reveal: () => {},
    conceal: () => {},
    destroy: () => {
      if (mount.parentNode) mount.parentNode.removeChild(mount);
    },
  };
}

/** True once the widget has painted something the visitor can actually see and click. */
function hasVisibleContent(mount: HTMLElement): boolean {
  const nodes = mount.querySelectorAll("*");
  for (let i = 0; i < nodes.length; i++) {
    const rect = nodes[i].getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return true;
  }
  return false;
}

export interface ExecuteTurnstileOptions {
  /**
   * Element to render the widget inside, in place of the full-window overlay. Must not be
   * `display:none`: Turnstile will not run in a hidden container.
   */
  container?: HTMLElement | null;
  /** Aborting settles the attempt as a cancellation. */
  signal?: AbortSignal;
}

/** Acquire a Turnstile token for `action`, regardless of `UGG_PUBLIC_CAPTCHA_PROVIDER`. */
export async function executeTurnstile(
  action: CaptchaAction,
  { container, signal }: ExecuteTurnstileOptions = {},
): Promise<CaptchaResult> {
  const sitekey = process.env.UGG_PUBLIC_TURNSTILE_SITEKEY;
  if (!sitekey) {
    throw captchaError("config", "UGG_PUBLIC_TURNSTILE_SITEKEY is not set");
  }

  await loadTurnstileScript();

  const win = getWindow();
  const doc = getDocument();
  const turnstile = win?.turnstile;
  if (!turnstile || !doc) {
    throw captchaError("unavailable", "turnstile is not present after script load");
  }
  if (signal?.aborted) {
    throw captchaError("cancelled", "aborted by caller");
  }

  return new Promise<CaptchaResult>((resolve, reject) => {
    const overlay = container
      ? createInlineHost(container)
      : createOverlay(doc, () => fail("cancelled", "dismissed by visitor"));
    const onAbort = () => fail("cancelled", "aborted by caller");
    signal?.addEventListener("abort", onAbort);
    let widgetId: string | undefined;
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    let lastErrorCode: string | null = null;
    let errorGraceArmed = false;

    const arm = (ms: number, failure: CaptchaFailure, detail: string) => {
      timer = setTimeout(() => fail(failure, detail), ms);
    };
    const disarm = () => {
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    };

    // Turnstile can paint a visible challenge without firing
    // before-interactive-callback, so watch the DOM as well: whatever notices first
    // reveals the backdrop and arms the Escape hatch.
    const visibility = new MutationObserver(() => {
      if (hasVisibleContent(overlay.mount)) overlay.reveal();
    });
    visibility.observe(overlay.mount, { childList: true, subtree: true, attributes: true });

    const teardown = () => {
      signal?.removeEventListener("abort", onAbort);
      disarm();
      visibility.disconnect();
      try {
        if (widgetId !== undefined) turnstile.remove(widgetId);
      } catch {
        // Widget already gone; the overlay still needs removing.
      }
      overlay.destroy();
    };

    const succeed = (token: string) => {
      if (settled) return;
      settled = true;
      teardown();
      resolve({ token, version: "turnstile" });
    };

    function fail(failure: CaptchaFailure, detail: string) {
      if (settled) return;
      settled = true;
      teardown();
      reject(captchaError(failure, detail));
    }

    try {
      widgetId = turnstile.render(overlay.mount, {
        sitekey,
        action,
        // Nothing runs, and nothing is visible, until we ask — and then only if the
        // visitor actually has to do something.
        execution: "execute",
        appearance: "interaction-only",
        // Let Turnstile retry. The 600xxx family ("generic challenge failure") is
        // documented as retryable and Turnstile recovers from it on its own; with
        // retry disabled, a transient failure became a hard one. Our timers still
        // bound the whole operation, so its retry loop cannot run away.
        retry: "auto",
        "response-field": false,
        theme: "dark",
        size: container ? "flexible" : "normal",
        callback: succeed,
        // Provisional, not fatal: Turnstile calls this and then retries. Settling here
        // would defeat that, so record the code and give the retry a bounded window —
        // a real token cancels it, and a genuinely fatal error surfaces the code.
        "error-callback": (code?: string) => {
          lastErrorCode = code || "unknown";
          console.warn(`Turnstile challenge error ${lastErrorCode}`);
          // Arm the grace window on the FIRST error only. Turnstile retries on its own
          // and re-fires this on every attempt, so re-arming each time would push the
          // deadline out indefinitely and the caller would spin forever.
          if (errorGraceArmed) return;
          errorGraceArmed = true;
          disarm();
          arm(ERROR_GRACE_MS, "challenge-failed", lastErrorCode);
        },
        "expired-callback": () => fail("expired", "token expired before use"),
        "timeout-callback": () => fail("timeout", "interactive challenge timed out"),
        "unsupported-callback": () => fail("unsupported", "browser unsupported"),
        "before-interactive-callback": () => {
          // Real progress, so a later error is allowed a fresh grace window.
          errorGraceArmed = false;
          // A human is now driving; swap the unattended budget for the longer one.
          disarm();
          arm(INTERACTIVE_TIMEOUT_MS, "timeout", "interactive challenge unanswered");
          overlay.reveal();
        },
        "after-interactive-callback": () => {
          errorGraceArmed = false;
          disarm();
          overlay.conceal();
          arm(UNATTENDED_TIMEOUT_MS, "timeout", `no token after ${UNATTENDED_TIMEOUT_MS}ms`);
        },
      });

      if (widgetId === undefined) {
        fail("unavailable", "turnstile.render returned no widget id");
        return;
      }

      arm(UNATTENDED_TIMEOUT_MS, "timeout", `no token after ${UNATTENDED_TIMEOUT_MS}ms`);
      turnstile.execute(widgetId);
    } catch (err) {
      fail("unavailable", String(err));
    }
  });
}
